import { FrequentlyAskedQuestionItemViewModel } from "client/Api/Hub/common";
import { FrequentlyAskedQuestionsItemLayout } from "client/Features/Hub/FrequentlyAskedQuestionsItemLayout";
import {
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useLayoutEffect,
	useRef
} from "react";
import styles from "./FrequentlyAskedQuestions.module.less";
import { applyTableStylesToViewer } from "client/Components/RichTextEditor/tableRendering";
import { RawHtml, Typography } from "client/Components";
import { useToggle } from "client/Hooks";
import { ContentViewerEmptyBlockBehavior } from "../../ContentViewer";
import { never } from "client_legacy/ndaw/errors";

export type FaqContentViewerEmptyBlockSettings = {
	question?: ContentViewerEmptyBlockBehavior;
	answer?: ContentViewerEmptyBlockBehavior;
};

export type FrequentlyAskedQuestionsProps = {
	items: readonly FrequentlyAskedQuestionItemViewModel[];
	emptySettings?: FaqContentViewerEmptyBlockSettings;
};

export const FrequentlyAskedQuestions = (props: FrequentlyAskedQuestionsProps): ReactElement => {
	const {
		items,
		emptySettings
	} = props;

	return (
		<ul className={styles.root}>
			{items.map(
				(x, index) => (
					<Item
						key={index}
						trackingId={generateTrackingId("faq-item", `|${index}`)}
						question={x.question}
						answer={x.answer}
						emptySettings={emptySettings}
					/>
				)
			)}
		</ul>
	);
};
FrequentlyAskedQuestions.displayName = "FrequentlyAskedQuestions";

type ItemProps = {
	question: string;
	answer: string;
	trackingId: string;
	emptySettings: FaqContentViewerEmptyBlockSettings | undefined;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		answer,
		question,
		trackingId,
		emptySettings
	} = props;

	const [isExpanded, toggleIsExpanded] = useToggle(false);

	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(answer);

			const container: HTMLDivElement = ensureNotNil(ref.current);
			applyTableStylesToViewer(container);
		},
		[answer]
	);

	const renderEmpty = (behavior: ContentViewerEmptyBlockBehavior | undefined): ReactElement | string => {
		if (!behavior || behavior.kind === "empty") {
			return "";
		}

		if (behavior.kind === "placeholder") {
			return (
				<Typography textColor="secondary">
					{behavior.placeholder}
				</Typography>
			);
		}

		never(behavior);
	};

	return (
		<li>
			<FrequentlyAskedQuestionsItemLayout
				trackingId={trackingId}
			>
				<FrequentlyAskedQuestionsItemLayout.Question
					trackingId="question"
					onClick={toggleIsExpanded}
					isExpanded={isExpanded}
				>
					<h4>
						{question || renderEmpty(emptySettings?.question)}
					</h4>
				</FrequentlyAskedQuestionsItemLayout.Question>
				<FrequentlyAskedQuestionsItemLayout.Answer
					trackingId="answer"
					isExpanded={isExpanded}
				>
					<div className={styles.html} ref={ref}>
						{answer
							? <RawHtml html={answer} />
							: renderEmpty(emptySettings?.answer)
						}
					</div>
				</FrequentlyAskedQuestionsItemLayout.Answer>
			</FrequentlyAskedQuestionsItemLayout>
		</li>
	);
};
Item.displayName = "Item";